import i18next from 'i18next';
import React, {useCallback} from 'react';

import {Select} from '@famly/mf_input_select';
import {Notice} from '@famly/mf_data-display_notice';
import {FormControl} from '@famly/mf_input_form-control';
import {Stack} from '@famly/mf_layout_stack';
import {Box} from '@famly/mf_layout_box';
import {isNonEmpty} from 'web-app/util/non-empty-array';
import {needsTranslations} from 'web-app/util/scoped-needs-translation';
import {hasValue} from '@famly/stat_ts-utils_has-value';

import type * as Types from '../types';
import {AppendField} from './append-field';
import {OptionAdornment} from './option-adornment';
import {EditTuitionButton} from './edit-tuition-button';

type TuitionsProps = {
    onChange: Types.HandleTuitionsChange;
    appendField: Types.AppendTuitionField;
    removeField: Types.RemoveTuitionField;
    toggleField: Types.ToggleTuitionField;
    tuitionsState: Types.State['tuitionsState'];
    tuitionOptions: Array<Types.TuitionAndFeeOption>;
    isDisabled: boolean;
    canOverridePrice: boolean;
    handleModalToggle: Types.HandleModalToggle;
};

export const Tuitions = ({
    onChange,
    appendField,
    removeField,
    toggleField,
    tuitionsState,
    tuitionOptions,
    isDisabled,
    canOverridePrice,
    handleModalToggle,
}: TuitionsProps) => {
    const {selectedTuitions, allSelectedTuitionsHaveTheSameFrequency, inputFields, fieldCurrentlyOpen} = tuitionsState;

    const shouldDisplayAddTuitionButton = useCallback(
        (index: number) => index === inputFields.length - 1 && isNonEmpty(selectedTuitions),
        [inputFields.length, selectedTuitions],
    );

    return (
        <FormControl label={i18next.t('react.simplifiedUsBillingPlan.translations.json.tuiton')}>
            <Stack direction="column" spacing={2}>
                {inputFields.map((field, index) => {
                    return (
                        <Box key={index}>
                            <Stack alignItems="center">
                                <Select
                                    fullWidth
                                    onChange={selectedOption => onChange(index, selectedOption)}
                                    options={tuitionOptions}
                                    getOptionAdornment={option =>
                                        option.type === 'add-new-item' ? undefined : (
                                            <OptionAdornment>
                                                $
                                                {hasValue(option.lowestAndHighestPrice.lowest)
                                                    ? `${option.lowestAndHighestPrice.lowest?.toFixed(2)} - `
                                                    : null}
                                                {option.lowestAndHighestPrice.highest.toFixed(2)}/{option.frequency}
                                            </OptionAdornment>
                                        )
                                    }
                                    value={field.id}
                                    data-e2e-class="tuition-select"
                                    open={fieldCurrentlyOpen === index}
                                    onClose={() => toggleField(null)}
                                    onOpen={() => toggleField(index)}
                                    disabled={isDisabled}
                                />
                                <EditTuitionButton
                                    index={index}
                                    canOverridePrice={canOverridePrice}
                                    onRemoveField={() => removeField(index)}
                                    fields={inputFields}
                                    selectedTuition={selectedTuitions.find(tuition => tuition.id === field.id)}
                                    handleModalToggle={handleModalToggle}
                                />
                            </Stack>
                            {shouldDisplayAddTuitionButton(index) ? (
                                <Box mt={2} alignSelf="start" data-e2e-id="add-tuition-button">
                                    <AppendField
                                        text={needsTranslations('SimplifiedUsBilling', 'Add a tuition')}
                                        appendField={() => appendField(index)}
                                    />
                                </Box>
                            ) : null}
                        </Box>
                    );
                })}
                {!allSelectedTuitionsHaveTheSameFrequency ? (
                    <Box mt={4} data-e2e-id="incompatible-frequency-notice">
                        <Notice
                            severity="error"
                            title={needsTranslations(
                                'SimplifiedUsBilling',
                                'Your selected tuitions are not compatible',
                            )}
                            text={needsTranslations(
                                'SimplifiedUsBilling',
                                'Please ensure that all selected tuitions have the same pricing period',
                            )}
                        />
                    </Box>
                ) : null}
            </Stack>
        </FormControl>
    );
};
